// src/App.jsx
import React, { useEffect, useState } from 'react';
import { Route, Routes, Navigate, useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';

// ✅ Zustand store
import useAuthStore from './stores/authStore';

import OtpVerify from './auth/OtpVerify';
import Login from './auth/Login';
import Signup from './auth/Signup';
import Protect from './Protect';
import ForgotPasswordForm from './auth/ForgotPasswordForm';
import ResetPasswordForm from './auth/ResetPasswordForm';
import LoginSuccess from './auth/LoginSuccess';
import LoginView from './auth/LoginView';

import Sidebar from './components/Sidebar';
import Header from './components/Header';
import UserSettings from './components/UserSettings';
import './App.css';

import AdminDashboard from './admin/dashboard';
import AdminEvents from './admin/events';
import AdminUsers from './admin/users';

import HostDashboard from './operator/dashboard';
import HostEvent from './operator/event';
import HostUsers from './operator/users';

import GuestDashboard from './enduser/dashboard';
import GuestEvent from './enduser/event';

import SstemSettings from './system/settings';

import FreeInvite from './pages/FreeInvite';
import InvitePrint from './pages/inviteprint';
import QrDisplay from './pages/qrdisplay';
import UserInvite from './auth/userinvite';
import EventPlusLandingPage from './pages/EventPlusLandingPage';

const App = () => {
  // ✅ propsのバケツリレーをやめ、storeから直接取得
  const { userName, userType, logout } = useAuthStore();

  const navigate = useNavigate();
  const location = useLocation();
  const isPopup = new URLSearchParams(location.search).get('popup') === 'true';
  const [isSidebarOpen, setSidebarOpen] = useState(false);

  const ALL_ROLES = ['enduser', 'operator', 'admin', 'system'];

  useEffect(() => {
    const ua = navigator.userAgent;
    const isChrome = /Chrome/i.test(ua) && !/Edg|OPR/i.test(ua);
    if (isChrome) {
      document.documentElement.style.touchAction = 'manipulation';
      document.documentElement.style.webkitTapHighlightColor = 'transparent';
      document.body.style.touchAction = 'manipulation';
      document.body.style.webkitTapHighlightColor = 'transparent';
    }
  }, []);

  const isAdmin  = userType === 'admin';
  const isHost   = userType === 'operator';
  const isGuest  = userType === 'enduser';
  const isSystem = userType === 'system';
  const hasSidebar = userName && !isPopup && !isGuest;

  const handleLogout = async () => {
    try {
      await axios.post(`${import.meta.env.VITE_API_URL}/api/logout/`);
    } catch (_) {
    } finally {
      logout();
      navigate('/login', { replace: true });
    }
  };

  const Layout = ({ children }) => (
    <div>
      <Header
        WuserName={userName}
        onLogout={handleLogout}
        onToggleSidebar={() => setSidebarOpen(v => !v)}
        userType={userType}
      />
      <div className={`main-content ${hasSidebar ? 'has-sidebar' : 'no-sidebar'}`}>
        {hasSidebar && (
          <Sidebar
            userType={userType}
            isOpen={isSidebarOpen}
            onClose={() => setSidebarOpen(false)}
          />
        )}
        <div className="content">{children}</div>
      </div>
    </div>
  );

  return (
    <div className="app-container">
      <Routes>
        {/* 認証前 — ✅ setWxxx のprops渡しが全部不要になった */}
        <Route path="/login"             element={<Login />} />
        <Route path="/otpverify"         element={<OtpVerify />} />
        <Route path="/signup"            element={<Signup />} />
        <Route path="/forgot-password"   element={<ForgotPasswordForm />} />
        <Route path="/reset-password"    element={<ResetPasswordForm />} />
        <Route path="/login-success"     element={<LoginSuccess />} />
        <Route path="/auth/login-view/:kind" element={<LoginView />} />

        <Route path="/components/UserSettings" element={<UserSettings />} />
        <Route path="/freeinvite/*"  element={<FreeInvite />} />
        <Route path="/inviteprint/*" element={<InvitePrint />} />
        <Route path="/qrdisplay/*"   element={<QrDisplay />} />
        <Route path="/userinvite/*"  element={<UserInvite />} />

        <Route path="/usersettings" element={
          <Protect allowTypes={ALL_ROLES}>
            <Layout><UserSettings /></Layout>
          </Protect>
        } />

        {/* ============== Admin（admin/system） ============== */}
        <Route path="/admin/*" element={
          <Protect allowTypes={['admin', 'system']}>
            <Layout>
              <Routes>
                <Route path="dashboard" element={<AdminDashboard />} />
                <Route path="events"    element={<AdminEvents />} />
                <Route path="users"     element={<AdminUsers />} />
                <Route path="*"         element={<Navigate to="dashboard" replace />} />
              </Routes>
            </Layout>
          </Protect>
        } />

        {/* ============== Host（operator/admin/system） ============== */}
        <Route path="/operator/*" element={
          <Protect allowTypes={['operator', 'admin', 'system']}>
            <Layout>
              <Routes>
                <Route path="dashboard"           element={<HostDashboard />} />
                <Route path="event/:invite_token" element={<HostEvent />} />
                <Route path="users"               element={<HostUsers />} />
                <Route path="*"                   element={<Navigate to="dashboard" replace />} />
              </Routes>
            </Layout>
          </Protect>
        } />

        {/* ============== Guest（enduser/operator/admin/system） ============== */}
        <Route path="/enduser/*" element={
          <Protect allowTypes={['enduser', 'operator', 'admin', 'system']}>
            <Layout>
              <Routes>
                <Route path="dashboard"           element={<GuestDashboard />} />
                <Route path="event/:invite_token" element={<GuestEvent />} />
                <Route path="*"                   element={<Navigate to="dashboard" replace />} />
              </Routes>
            </Layout>
          </Protect>
        } />

        {/* ============== System（systemのみ） ============== */}
        <Route path="/system/*" element={
          <Protect allowTypes={['system']}>
            <Layout>
              <Routes>
                <Route path="settings" element={<SstemSettings />} />
                <Route path="*"        element={<Navigate to="settings" replace />} />
              </Routes>
            </Layout>
          </Protect>
        } />

        {/* ルート誘導 */}
        <Route path="/" element={
          isAdmin  ? <Navigate to="/admin/dashboard" replace /> :
          isHost   ? <Navigate to="/operator/dashboard"  replace /> :
          isGuest  ? <Navigate to="/enduser/dashboard" replace /> :
          isSystem ? <Navigate to="/admin/dashboard" replace /> :
                     <EventPlusLandingPage />
        } />

        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </div>
  );
};

export default App;